import React, { useState, useEffect, useMemo, useContext } from 'react';
import {
  ThemeProvider,
  createTheme,
  CssBaseline,
  Box,
  Drawer,
  Toolbar,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  IconButton,
  Paper,
  Card,
  CardContent,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Chip,
  Switch,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Alert,
  Divider,
  useMediaQuery,
  FormControlLabel,
  Fade,
  Slide,
  Grow,
  Menu,
  MenuItem,
  AlertColor
} from '@mui/material';
import {
  LayoutDashboard,
  Package,
  ShoppingCart,
  DollarSign,
  Settings,
  BarChart2,
  UserCircle,
  Bell,
  ImagePlus,
  Edit3,
  Trash2,
  Search,
  Download,
  Upload,
  Eye,
  ChevronLeft,
  TrendingUp,
  Activity,
  CheckCircle,
  AlertTriangle,
  PlusCircle,
  MapPin,
  CreditCard
} from 'lucide-react';

// ======================================
// Theme Configuration
// ======================================
const theme = createTheme({
  palette: {
    primary: { main: '#1976d2', light: '#42a5f5', dark: '#1565c0' },
    secondary: { main: '#42a5f5', light: '#90caf9', dark: '#1976d2' },
    background: { default: '#f8fafc', paper: '#ffffff' },
    text: { primary: '#1e293b', secondary: '#64748b' },
    divider: '#e2e8f0',
    success: { main: '#10b981', light: '#34d399' },
    warning: { main: '#f59e0b', light: '#fbbf24' },
    error: { main: '#ef4444', light: '#f87171' }
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: { fontWeight: 700, letterSpacing: '-0.025em' },
    h5: { fontWeight: 600, letterSpacing: '-0.025em' },
    h6: { fontWeight: 600, letterSpacing: '-0.025em' },
    body1: { fontSize: '0.875rem', lineHeight: 1.6 }
  },
  shape: { borderRadius: 12 },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
          border: '1px solid #f1f5f9',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
            transform: 'translateY(-2px)'
          }
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
          border: '1px solid #f1f5f9'
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 500,
          padding: '8px 16px',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': { transform: 'translateY(-1px)' }
        },
        contained: {
          boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
          '&:hover': {
            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)'
          }
        }
      }
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            '& fieldset': { borderColor: '#e2e8f0' },
            '&:hover fieldset': { borderColor: '#cbd5e1' },
            '&.Mui-focused fieldset': {
              borderColor: '#1976d2',
              borderWidth: '2px',
              boxShadow: '0 0 0 3px rgb(25 118 210 / 0.1)'
            }
          }
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 500,
          fontSize: '0.75rem'
        }
      }
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          margin: '2px 8px',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '&.Mui-selected': {
            backgroundColor: 'rgba(25, 118, 210, 0.08)',
            borderLeft: '3px solid #1976d2',
            '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.12)' },
            '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
              color: '#1976d2',
              fontWeight: 600
            }
          },
          '&:hover': {
            backgroundColor: 'rgba(148, 163, 184, 0.1)',
            transform: 'translateX(4px)'
          }
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          backgroundColor: '#f8fafc',
          fontWeight: 600,
          color: '#374151',
          borderBottom: '1px solid #e5e7eb'
        }
      }
    }
  }
});

// ======================================
// Data Interfaces
// ======================================
interface Order {
  id: string;
  customerName: string;
  status: 'Pending' | 'Preparing' | 'Ready' | 'Dispatched' | 'Delivered' | 'Cancelled';
  total: number;
  date: string;
  items: { name: string; quantity: number }[];
  deliveryAddress: string;
}

interface Product {
  id: string;
  name: string;
  category: string;
  price: number;
  stock: number;
  imageUrl?: string;
  description: string;
  isAvailable: boolean;
}

interface BusinessInfo {
  name: string;
  address: string;
  phone: string;
  email: string;
  description: string;
  logoUrl?: string;
  verificationStatus: 'Verified' | 'Pending' | 'Rejected';
  commissionRate: number;
}

interface Payout {
  id: string;
  date: string;
  amount: number;
  status: 'Pending' | 'Completed' | 'Failed';
}

interface NotificationAlert {
  id: string;
  type: 'Order Update' | 'System Alert' | 'New Review' | 'Low Stock';
  message: string;
  timestamp: string;
  read: boolean;
  link?: string;
}

interface StoreHours {
  day: 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday' | 'Sunday';
  openTime: string;
  closeTime: string;
  isOpen: boolean;
}

// ======================================
// Sample Data
// ======================================
const sampleOrders: Order[] = [
  { 
    id: 'ORD001', 
    customerName: 'Tapiwa Hweshe', 
    status: 'Pending', 
    total: 25.50, 
    date: '2024-05-28', 
    items: [{ name: 'Pizza', quantity: 1 }], 
    deliveryAddress: '123 Rabbit Hole' 
  },
  { 
    id: 'ORD002', 
    customerName: 'Mukomana wepaZim', 
    status: 'Preparing', 
    total: 15.00, 
    date: '2024-05-28', 
    items: [{ name: 'Burger', quantity: 2 }], 
    deliveryAddress: '456 Fixit Ave' 
  }
];

const sampleProducts: Product[] = [
  { 
    id: 'PROD001', 
    name: 'Margherita Pizza', 
    category: 'Italian', 
    price: 12.99, 
    stock: 50, 
    imageUrl: 'https://images.unsplash.com/photo-1565299624946-b28f40a0ca4b?w=100&h=100&fit=crop&crop=center', 
    description: 'Classic cheese and tomato pizza.', 
    isAvailable: true 
  },
  { 
    id: 'PROD002', 
    name: 'Cheeseburger', 
    category: 'Fast Food', 
    price: 8.50, 
    stock: 5, 
    imageUrl: 'https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=100&h=100&fit=crop&crop=center', 
    description: 'Beef patty with cheese, lettuce, and tomato.', 
    isAvailable: true 
  }
];

const initialBusinessInfo: BusinessInfo = {
  name: "The Blue Plate",
  address: "123 Foodie Lane, Flavor Town, FT 54321",
  phone: "555-0101",
  email: "contact@blueplate.com",
  description: "Serving the best dishes in town with fresh ingredients and passion.",
  logoUrl: 'https://images.unsplash.com/photo-1571091718767-18b5b1457add?w=150&h=150&fit=crop&crop=center',
  verificationStatus: 'Verified',
  commissionRate: 15,
};

const samplePayouts: Payout[] = [
  { id: 'PAY001', date: '2024-05-20', amount: 1250.75, status: 'Completed' }
];

const sampleNotifications: NotificationAlert[] = [
  { 
    id: 'NOTIF001', 
    type: 'Order Update', 
    message: 'Order ORD002 status changed to Preparing.', 
    timestamp: '2024-05-28 11:00', 
    read: false, 
    link: '/orders/ORD002' 
  }
];

const initialStoreHours: StoreHours[] = [
  { day: 'Monday', openTime: '09:00', closeTime: '21:00', isOpen: true },
  { day: 'Tuesday', openTime: '09:00', closeTime: '21:00', isOpen: true },
  { day: 'Wednesday', openTime: '09:00', closeTime: '21:00', isOpen: true },
  { day: 'Thursday', openTime: '09:00', closeTime: '21:00', isOpen: true },
  { day: 'Friday', openTime: '09:00', closeTime: '22:00', isOpen: true },
  { day: 'Saturday', openTime: '10:00', closeTime: '22:00', isOpen: true },
  { day: 'Sunday', openTime: '10:00', closeTime: '20:00', isOpen: true }
];

// ======================================
// Constants & Helpers
// ======================================
const drawerWidth = 280;

const getStatusChipColor = (status: Order['status'] | Payout['status']) => {
  switch (status) {
    case 'Pending': return 'warning';
    case 'Preparing': return 'info';
    case 'Ready': return 'secondary';
    case 'Dispatched': return 'primary';
    case 'Delivered': return 'success';
    case 'Completed': return 'success';
    case 'Cancelled': return 'error';
    case 'Failed': return 'error';
    default: return 'default';
  }
};

// ======================================
// Global Alert System
// ======================================
interface SnackbarState {
  open: boolean;
  message: string;
  severity: AlertColor;
}

interface ConfirmDialogState {
  open: boolean;
  title: string;
  message: string;
  onConfirm: () => void;
}

interface GlobalAlertContextType {
  showSnackbar: (message: string, severity?: AlertColor) => void;
  showConfirmDialog: (title: string, message: string, onConfirm: () => void) => void;
}

const GlobalAlertContext = React.createContext<GlobalAlertContextType | undefined>(undefined);

const useGlobalAlerts = () => {
  const context = useContext(GlobalAlertContext);
  if (!context) {
    throw new Error('useGlobalAlerts must be used within a GlobalAlertProvider');
  }
  return context;
};

// ======================================
// Component Props
// ======================================
interface SectionProps {
  showSnackbar: (message: string, severity?: AlertColor) => void;
  showConfirmDialog: (title: string, message: string, onConfirm: () => void) => void;
  navigateTo?: (section: string) => void;
}

// ======================================
// Reusable Components
// ======================================
const StatsCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  value: string | number;
  subtitle?: string;
  trend?: { value: number; isPositive: boolean };
  onClick?: () => void;
}> = ({ icon, title, value, subtitle, trend, onClick }) => (
  <Grow in timeout={300}>
    <Card 
      sx={{ 
        cursor: onClick ? 'pointer' : 'default',
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: 'linear-gradient(90deg, #1976d2, #42a5f5)',
        }
      }}
      onClick={onClick}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ 
            p: 1.5, 
            borderRadius: 2, 
            backgroundColor: 'rgba(25, 118, 210, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {icon}
          </Box>
          {trend && (
            <Chip
              size="small"
              icon={<TrendingUp size={14} />}
              label={`${trend.isPositive ? '+' : ''}${trend.value}%`}
              color={trend.isPositive ? 'success' : 'error'}
              variant="outlined"
            />
          )}
        </Box>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, color: '#1e293b' }}>
          {value}
        </Typography>
        <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500, mb: 1 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" sx={{ color: '#94a3b8' }}>
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  </Grow>
);

// ======================================
// Section Components
// ======================================
const DashboardOverviewSection: React.FC<SectionProps> = ({ showSnackbar, navigateTo }) => {
  const totalOrders = sampleOrders.length;
  const totalRevenue = sampleOrders.reduce((sum, order) => 
    order.status === 'Delivered' ? sum + order.total : sum, 0
  );
  const pendingOrders = sampleOrders.filter(o => 
    o.status === 'Pending' || o.status === 'Preparing'
  );
  const recentActivity = sampleNotifications.slice(0, 3);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b', mb: 1 }}>
            Dashboard Overview
          </Typography>
          <Typography variant="body1" sx={{ color: '#64748b' }}>
            Welcome back! Here's what's happening with your business today.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Activity size={18} />}
          sx={{ borderRadius: 2 }}
          onClick={() => navigateTo && navigateTo('financial')}
        >
          View Analytics
        </Button>
      </Box>

      <Box sx={{ 
        display: 'flex', 
        gap: 3, 
        flexWrap: 'wrap',
        '& > *': { flex: 1, minWidth: '280px' }
      }}>
        <StatsCard
          icon={<ShoppingCart size={24} color={theme.palette.primary.main} />}
          title="Total Orders"
          value={totalOrders}
          subtitle="All time orders"
          trend={{ value: 12, isPositive: true }}
        />
        <StatsCard
          icon={<DollarSign size={24} color={theme.palette.success.main} />}
          title="Total Revenue"
          value={`$${totalRevenue.toFixed(2)}`}
          subtitle="From delivered orders"
          trend={{ value: 8, isPositive: true }}
        />
        <StatsCard
          icon={<AlertTriangle size={24} color={theme.palette.warning.main} />}
          title="Pending Orders"
          value={pendingOrders.length}
          subtitle="Requiring attention"
          onClick={() => navigateTo && navigateTo('orders')}
        />
      </Box>

      <Box sx={{ 
        display: 'flex', 
        gap: 3, 
        flexDirection: { xs: 'column', lg: 'row'},
        '& > *': { flex: 1 }
      }}>
        <Fade in timeout={600}>
          <Paper sx={{ 
            p: 3, 
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            borderLeft: '4px solid #1976d2'
          }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1e293b' }}>
              Orders Requiring Action
            </Typography>
            {pendingOrders.length > 0 ? (
              <List sx={{ p: 0 }}>
                {pendingOrders.slice(0, 5).map((order, index) => (
                  <Slide key={order.id} direction="right" in timeout={300 * (index + 1)}>
                    <ListItem 
                      sx={{ 
                        px: 0, 
                        py: 1.5,
                        borderRadius: 2,
                        '&:hover': { backgroundColor: 'rgba(148, 163, 184, 0.05)' }
                      }}
                      secondaryAction={
                        <Chip 
                          label={order.status} 
                          color={getStatusChipColor(order.status)} 
                          size="small"
                        />
                      }
                    >
                      <ListItemText 
                        primary={
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            Order {order.id} - {order.customerName}
                          </Typography>
                        }
                        secondary={`$${order.total.toFixed(2)}`}
                      />
                    </ListItem>
                  </Slide>
                ))}
              </List>
            ) : (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <CheckCircle size={48} color={theme.palette.success.main} />
                <Typography sx={{ mt: 1, color: '#64748b' }}>
                  No pending orders
                </Typography>
              </Box>
            )}
          </Paper>
        </Fade>

        <Fade in timeout={800}>
          <Paper sx={{ 
            p: 3,
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            borderLeft: '4px solid #42a5f5'
          }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1e293b' }}>
              Recent Activity
            </Typography>
            {recentActivity.length > 0 ? (
              <List sx={{ p: 0 }}>
                {recentActivity.map((activity, index) => (
                  <Slide key={activity.id} direction="left" in timeout={300 * (index + 1)}>
                    <ListItem sx={{ px: 0, py: 1.5 }}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <Box sx={{ 
                          p: 1, 
                          borderRadius: '50%', 
                          backgroundColor: 'rgba(66, 165, 245, 0.1)' 
                        }}>
                          <Bell size={16} color={theme.palette.secondary.main} />
                        </Box>
                      </ListItemIcon>
                      <ListItemText 
                        primary={
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {activity.message}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                            {new Date(activity.timestamp).toLocaleString()}
                          </Typography>
                        }
                      />
                    </ListItem>
                  </Slide>
                ))}
              </List>
            ) : (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Activity size={48} color={theme.palette.info.main} />
                <Typography sx={{ mt: 1, color: '#64748b' }}>
                  No recent activity
                </Typography>
              </Box>
            )}
          </Paper>
        </Fade>
      </Box>
    </Box>
  );
};

const ProductManagementSection: React.FC<SectionProps> = ({ showSnackbar, showConfirmDialog }) => {
const [products, setProducts] = useState<Product[]>(sampleProducts);
const [editingProduct, setEditingProduct] = useState<Product | null>(null);
const [searchTerm, setSearchTerm] = useState('');
const [categoryFilter, setCategoryFilter] = useState('');
const [dialogOpen, setDialogOpen] = useState(false);
const [productForm, setProductForm] = useState<Partial<Product>>({ 
  name: '', category: '', price: 0, stock: 0, description: '', isAvailable: true, imageUrl: '' 
});

const handleOpenProductDialog = (product?: Product) => {
  if (product) {
    setEditingProduct(product);
    setProductForm({ ...product });
  } else {
    setEditingProduct(null);
    setProductForm({ 
      name: '', 
      category: '', 
      price: 0, 
      stock: 0, 
      description: '', 
      isAvailable: true, 
      imageUrl: 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=100&h=100&fit=crop&crop=center' 
    });
  }
  setDialogOpen(true);
};

const handleCloseProductDialog = () => { 
  setDialogOpen(false);
  setEditingProduct(null);
  setProductForm({ 
    name: '', 
    category: '', 
    price: 0, 
    stock: 0, 
    description: '', 
    isAvailable: true, 
    imageUrl: '' 
  });
};

  const handleSaveProduct = () => {
    if (editingProduct) {
      setProducts(products.map(p => 
        p.id === editingProduct.id ? { ...editingProduct, ...productForm } as Product : p
      ));
      showSnackbar('Product updated successfully!', 'success');
    } else {
      const newProduct = {
        ...productForm,
        id: `PROD${Date.now()}`,
        stock: Number(productForm.stock),
        price: Number(productForm.price)
      } as Product;
      setProducts([...products, newProduct]);
      showSnackbar('Product added successfully!', 'success');
    }
    handleCloseProductDialog();
  };

  const handleDeleteProduct = (productId: string) => {
    showConfirmDialog(
      'Delete Product?', 
      'Are you sure you want to delete this product? This action cannot be undone.',
      () => {
        setProducts(products.filter(p => p.id !== productId));
        showSnackbar('Product deleted.', 'info');
      }
    );
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const reader = new FileReader();
      reader.onload = (e) => setProductForm({ ...productForm, imageUrl: e.target?.result as string });
      reader.readAsDataURL(event.target.files[0]);
    }
  };

  const filteredProducts = products.filter(p => 
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) && 
    (categoryFilter === '' || p.category === categoryFilter)
  );

  const categories = useMemo(() => 
    Array.from(new Set(products.map(p => p.category))), [products]
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b', mb: 1 }}>
            Product Management
          </Typography>
          <Typography variant="body1" sx={{ color: '#64748b' }}>
            Manage your menu items and inventory
          </Typography>
        </Box>
      </Box>

      <Paper sx={{
        p: 3,
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        border: '1px solid #e2e8f0'
      }}>
        <Box sx={{
          display: 'flex',
          gap: 2,
          mb: 3,
          flexWrap: 'wrap',
          alignItems: 'center'
        }}>
          <TextField
            label="Search Products"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <Search size={20} style={{ marginRight: 8, color: '#64748b' }} />
            }}
            sx={{ flexGrow: 1, minWidth: '200px' }} />
          <TextField
            select
            label="Filter by Category"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            variant="outlined"
            size="small"
            sx={{ minWidth: '180px' }}
          >
            <MenuItem value=""><em>All Categories</em></MenuItem>
            {categories.map(cat => <MenuItem key={cat} value={cat}>{cat}</MenuItem>)}
          </TextField>
          <Button
            variant="contained"
            startIcon={<PlusCircle size={18} />}
            onClick={() => handleOpenProductDialog()}
            sx={{
              background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
              px: 3
            }}
          >
            Add Product
          </Button>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Upload size={16} />}
            onClick={() => showSnackbar('Bulk Import (mock)', 'info')}
          >
            Bulk Import
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Download size={16} />}
            onClick={() => showSnackbar('Bulk Export (mock)', 'info')}
          >
            Bulk Export
          </Button>
        </Box>
      </Paper>

      <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Image</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Price</TableCell>
              <TableCell>Stock</TableCell>
              <TableCell>Available</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredProducts.map((product, index) => (
              <Fade key={product.id} in timeout={200 * (index + 1)}>
                <TableRow
                  sx={{
                    '&:hover': {
                      backgroundColor: 'rgba(148, 163, 184, 0.05)',
                      transform: 'scale(1.01)',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                    }
                  }}
                >
                  <TableCell>
                    <Avatar
                      src={product.imageUrl || 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=40&h=40&fit=crop&crop=center'}
                      alt={product.name}
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 2,
                        border: '2px solid #f1f5f9'
                      }} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                      {product.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={product.category}
                      variant="outlined"
                      size="small"
                      sx={{ borderRadius: 2 }} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#059669' }}>
                      ${product.price.toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: product.stock <= 5 ? theme.palette.error.main :
                            product.stock <= 10 ? theme.palette.warning.main :
                              theme.palette.success.main,
                          fontWeight: 600
                        }}
                      >
                        {product.stock}
                      </Typography>
                      {product.stock <= 5 && (
                        <AlertTriangle size={16} color={theme.palette.error.main} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={product.isAvailable}
                      onChange={(e) => setProducts(products.map(p => p.id === product.id ? { ...p, isAvailable: e.target.checked } : p
                      ))}
                      size="small"
                      sx={{
                        '& .MuiSwitch-thumb': {
                          boxShadow: '0 2px 4px 0 rgb(0 35 11 / 0.2)',
                        },
                      }} />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton
                        size="small"
                        onClick={() => {
                          // Reset form state before opening dialog
                          setProductForm({
                            name: '',
                            category: '',
                            price: 0,
                            stock: 0,
                            description: '',
                            isAvailable: true,
                            imageUrl: ''
                          });
                          handleOpenProductDialog(product);
                        } }
                        sx={{
                          color: theme.palette.primary.main,
                          '&:hover': {
                            backgroundColor: 'rgba(25, 118, 210, 0.1)',
                            transform: 'scale(1.1)'
                          }
                        }}
                      >
                        <Edit3 size={18} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteProduct(product.id)}
                        sx={{
                          color: theme.palette.error.main,
                          '&:hover': {
                            backgroundColor: 'rgba(239, 68, 68, 0.1)',
                            transform: 'scale(1.1)'
                          }
                        }}
                      >
                        <Trash2 size={18} />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              </Fade>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {filteredProducts.length === 0 && (
        <Paper sx={{ p: 6, textAlign: 'center', backgroundColor: '#fafafa' }}>
          <Package size={64} color={theme.palette.text.secondary} />
          <Typography variant="h6" sx={{ mt: 2, mb: 1, color: '#64748b' }}>
            No products found
          </Typography>
          <Typography variant="body2" sx={{ color: '#94a3b8', mb: 3 }}>
            Try adjusting your search or filter criteria
          </Typography>
          <Button
            variant="contained"
            startIcon={<PlusCircle size={18} />}
            onClick={() => handleOpenProductDialog()}
          >
            Add Your First Product
          </Button>
        </Paper>
      )}

      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: dialogOpen ? 'flex' : 'none',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1300,
        }}
        onClick={handleCloseProductDialog}
      >
        <Box
          sx={{
            backgroundColor: '#ffffff',
            borderRadius: 3,
            maxWidth: '800px',
            width: '100%',
            m: 2,
            maxHeight: '90vh',
            overflowY: 'auto',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <DialogTitle sx={{
            pb: 1,
            fontSize: '1.25rem',
            fontWeight: 700,
            background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
            color: 'white',
            m: 0
          }}>
            {editingProduct ? 'Edit Product' : 'Add New Product'}
          </DialogTitle>
          <DialogContent sx={{ p: 3, mt: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                <TextField
                  label="Product Name"
                  fullWidth
                  variant="outlined"
                  value={productForm.name}
                  onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                  sx={{ flex: 2 }} />
                <TextField
                  label="Category"
                  fullWidth
                  variant="outlined"
                  value={productForm.category}
                  onChange={(e) => setProductForm({ ...productForm, category: e.target.value })}
                  sx={{ flex: 1 }} />
              </Box>

              <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                <TextField
                  label="Price"
                  type="number"
                  fullWidth
                  variant="outlined"
                  value={productForm.price}
                  onChange={(e) => setProductForm({ ...productForm, price: parseFloat(e.target.value) })}
                  InputProps={{ startAdornment: <Typography sx={{ mr: 1, color: '#64748b' }}>$</Typography> }} />
                <TextField
                  label="Stock Quantity"
                  type="number"
                  fullWidth
                  variant="outlined"
                  value={productForm.stock}
                  onChange={(e) => setProductForm({ ...productForm, stock: parseInt(e.target.value) })} />
              </Box>

              <TextField
                label="Description"
                multiline
                rows={4}
                fullWidth
                variant="outlined"
                value={productForm.description}
                onChange={(e) => setProductForm({ ...productForm, description: e.target.value })} />

              <Paper sx={{ p: 3, backgroundColor: '#f8fafc', border: '1px dashed #cbd5e1' }}>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                  Product Image
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <Avatar
                    src={productForm.imageUrl}
                    sx={{
                      width: 100,
                      height: 100,
                      borderRadius: 3,
                      border: '3px solid #e2e8f0'
                    }} />
                  <Box>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<ImagePlus size={18} />}
                      sx={{ mb: 1 }}
                    >
                      Upload Image
                      <input type="file" hidden accept="image/*" onChange={handleImageUpload} />
                    </Button>
                    <Typography variant="caption" sx={{ display: 'block', color: '#64748b' }}>
                      Recommended: 400x400px, JPG or PNG
                    </Typography>
                  </Box>
                </Box>
              </Paper>

              <FormControlLabel
                control={<Switch
                  checked={productForm.isAvailable ?? true}
                  onChange={(e) => setProductForm({ ...productForm, isAvailable: e.target.checked })} />}
                label={<Typography sx={{ fontWeight: 500 }}>
                  Product Available for Orders
                </Typography>} />
            </Box>
          </DialogContent>
      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button
          onClick={handleCloseProductDialog}
          variant="outlined"
          sx={{ borderRadius: 2 }}>
          Cancel
        </Button>
        <Button
          onClick={handleSaveProduct}
          variant="contained"
          sx={{
            borderRadius: 2,
            background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
            px: 3
          }}
        >
          {editingProduct ? 'Save Changes' : 'Add Product'}
        </Button>
      </DialogActions>
      </Box>
    </Box>
    </Box>
  );
};

const OrderManagementSection: React.FC<SectionProps> = ({ showSnackbar }) => {
  const [orders, setOrders] = useState<Order[]>(sampleOrders);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateRange, setDateRange] = useState<{ start: string, end: string }>({ start: '', end: '' });
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [statusMenuAnchorEl, setStatusMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [orderToUpdate, setOrderToUpdate] = useState<Order | null>(null);

  const handleViewOrderDetails = (order: Order) => setSelectedOrder(order);
  const handleCloseOrderDetails = () => setSelectedOrder(null);
  const handleOpenStatusMenu = (event: React.MouseEvent<HTMLElement>, order: Order) => { 
    setStatusMenuAnchorEl(event.currentTarget); 
    setOrderToUpdate(order); 
  };
  const handleCloseStatusMenu = () => { 
    setStatusMenuAnchorEl(null); 
    setOrderToUpdate(null); 
  };
  const handleUpdateStatus = (newStatus: Order['status']) => {
    if (orderToUpdate) {
      setOrders(orders.map(o => 
        o.id === orderToUpdate.id ? {...o, status: newStatus } : o
      ));
      showSnackbar(`Order ${orderToUpdate.id} status updated to ${newStatus}.`, 'success');
    }
    handleCloseStatusMenu();
  };
  
  const filteredOrders = orders.filter(order => {
    const matchesSearch = order.id.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         order.customerName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === '' || order.status === statusFilter;
    const orderDate = new Date(order.date);
    const startDate = dateRange.start ? new Date(dateRange.start) : null;
    const endDate = dateRange.end ? new Date(dateRange.end) : null;
    const matchesDate = (!startDate || orderDate >= startDate) && 
                       (!endDate || orderDate <= endDate);
    return matchesSearch && matchesStatus && matchesDate;
  });
  
  const orderStatuses: Order['status'][] = [
    'Pending', 'Preparing', 'Ready', 'Dispatched', 'Delivered', 'Cancelled'
  ];
  return (
    <Box>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b', mb: 1 }}>
            Order Management
          </Typography>
          <Typography variant="body1" sx={{ color: '#64748b' }}>
            Track and manage customer orders
          </Typography>
        </Box>
    </Box>

      <Paper sx={{ 
        p: 3, 
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        border: '1px solid #e2e8f0'
      }}>
        <Box sx={{ 
          display: 'flex', 
          gap: 2, 
          mb: 3, 
          flexWrap: 'wrap',
          alignItems: 'center'
        }}>
          <TextField 
            label="Search Orders" 
            variant="outlined" 
            size="small" 
            value={searchTerm} 
            onChange={(e) => setSearchTerm(e.target.value)} 
            InputProps={{ 
              startAdornment: <Search size={20} style={{ marginRight: 8, color: '#64748b' }} /> 
            }} 
            sx={{ flexGrow: 1, minWidth: '250px' }} 
          />
          <TextField 
            select 
            label="Filter by Status" 
            value={statusFilter} 
            onChange={(e) => setStatusFilter(e.target.value)} 
            variant="outlined" 
            size="small" 
            sx={{ minWidth: '180px' }}
          >
            <MenuItem value=""><em>All Statuses</em></MenuItem>
            {orderStatuses.map(stat => <MenuItem key={stat} value={stat}>{stat}</MenuItem>)}
          </TextField>
          <TextField 
            label="Start Date" 
            type="date" 
            size="small" 
            value={dateRange.start} 
            onChange={(e) => setDateRange({...dateRange, start: e.target.value})} 
            InputLabelProps={{ shrink: true }} 
            sx={{ minWidth: '160px' }}
          />
          <TextField 
            label="End Date" 
            type="date" 
            size="small" 
            value={dateRange.end} 
            onChange={(e) => setDateRange({...dateRange, end: e.target.value})} 
            InputLabelProps={{ shrink: true }} 
            sx={{ minWidth: '160px' }}
          />
        </Box>
      </Paper>

      <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Order ID</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Total</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredOrders.map((order, index) => (
              <Fade key={order.id} in timeout={200 * (index + 1)}>
                <TableRow 
                  sx={{ 
                    '&:hover': { 
                      backgroundColor: 'rgba(148, 163, 184, 0.05)',
                      transform: 'scale(1.01)',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                    }
                  }}
                >
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                      {order.id}
                    </Typography>
                  </TableCell>
                  <TableCell>{order.customerName}</TableCell>
                  <TableCell>{new Date(order.date).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#059669' }}>
                      ${order.total.toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={order.status} 
                      color={getStatusChipColor(order.status)} 
                      size="small"
                      sx={{ fontWeight: 500 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton 
                        size="small" 
                        onClick={() => handleViewOrderDetails(order)}
                        sx={{ 
                          color: theme.palette.primary.main,
                          '&:hover': { 
                            backgroundColor: 'rgba(25, 118, 210, 0.1)',
                            transform: 'scale(1.1)'
                          }
                        }}
                      >
                        <Eye size={18} />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        onClick={(e) => handleOpenStatusMenu(e, order)}
                        sx={{ 
                          color: theme.palette.info.main,
                          '&:hover': { 
                            backgroundColor: 'rgba(59, 130, 246, 0.1)',
                            transform: 'scale(1.1)'
                          }
                        }}
                      >
                        <Edit3 size={18} />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              </Fade>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {filteredOrders.length === 0 && (
        <Paper sx={{ p: 6, textAlign: 'center', backgroundColor: '#fafafa' }}>
          <ShoppingCart size={64} color={theme.palette.text.secondary} />
          <Typography variant="h6" sx={{ mt: 2, mb: 1, color: '#64748b' }}>
            No orders found
          </Typography>
          <Typography variant="body2" sx={{ color: '#94a3b8', mb: 3 }}>
            Try adjusting your search or filter criteria
          </Typography>
        </Paper>
      )}

        <Menu 
          anchorEl={statusMenuAnchorEl}
          open={Boolean(statusMenuAnchorEl)}
          onClose={handleCloseStatusMenu}
        >
          {orderStatuses.map(status => (
            <MenuItem 
              key={status} 
              onClick={() => handleUpdateStatus(status)}
            >
              {status}
            </MenuItem>
          ))}
        </Menu>
  
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: selectedOrder ? 'flex' : 'none',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1300,
          }}
          onClick={handleCloseOrderDetails}
        >
          <Box
            sx={{
              backgroundColor: '#ffffff',
              borderRadius: 3,
              maxWidth: '800px',
              width: '100%',
              m: 2,
              maxHeight: '90vh',
              overflowY: 'auto',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {selectedOrder && (
              <>
                <DialogTitle sx={{ 
                  pb: 1, 
                  fontSize: '1.25rem', 
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                  color: 'white',
                  m: 0
                }}>
                  Order Details - {selectedOrder.id}
                </DialogTitle>
                <DialogContent sx={{ p: 3, mt: 2 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#64748b' }}>
                          Customer
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {selectedOrder.customerName}
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#64748b' }}>
                          Date
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {new Date(selectedOrder.date).toLocaleString()}
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#64748b' }}>
                          Status
                        </Typography>
                        <Chip 
                          label={selectedOrder.status} 
                          color={getStatusChipColor(selectedOrder.status)} 
                          size="small"
                          sx={{ fontWeight: 500 }}
                        />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#64748b' }}>
                          Total
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500, color: '#059669' }}>
                          ${selectedOrder.total.toFixed(2)}
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#64748b', mb: 1 }}>
                        Delivery Address
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {selectedOrder.deliveryAddress}
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#64748b', mb: 1 }}>
                        Items
                      </Typography>
                      <List dense sx={{ p: 0 }}>
                        {selectedOrder.items.map((item, index) => (
                          <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                            <ListItemText 
                              primary={
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {item.name} (x{item.quantity})
                                </Typography>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  </Box>
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 0 }}>
                  <Button 
                    onClick={handleCloseOrderDetails}
                    variant="outlined"
                    sx={{ borderRadius: 2 }}
                  >
                    Close
                  </Button>
                </DialogActions>
              </>
            )}
          </Box>
        </Box>
      </Box>
      
  );
};

const FinancialManagementSection: React.FC<SectionProps> = ({ showSnackbar }) => {
  const [payouts, setPayouts] = useState<Payout[]>(samplePayouts);
  const totalRevenue = sampleOrders.reduce((sum, order) => 
    order.status === 'Delivered' ? sum + order.total : sum, 0
  );
  const pendingEarnings = payouts.filter(p => p.status === 'Pending')
    .reduce((sum, p) => sum + p.amount, 0);
  
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b', mb: 1 }}>
            Financial Management
          </Typography>
          <Typography variant="body1" sx={{ color: '#64748b' }}>
            Track your revenue and payouts
          </Typography>
        </Box>
      </Box>

      <Box sx={{ 
        display: 'flex', 
        gap: 3, 
        flexWrap: 'wrap',
        '& > *': { flex: 1, minWidth: '280px' }
      }}>
        <StatsCard
          icon={<DollarSign size={24} color={theme.palette.success.main} />}
          title="Total Revenue"
          value={`$${totalRevenue.toFixed(2)}`}
          subtitle="All time"
          trend={{ value: 15, isPositive: true }}
        />
        <StatsCard
          icon={<CreditCard size={24} color={theme.palette.info.main} />}
          title="Pending Earnings"
          value={`$${pendingEarnings.toFixed(2)}`}
          subtitle="To be paid out"
          trend={{ value: 8, isPositive: true }}
        />
        <StatsCard
          icon={<TrendingUp size={24} color={theme.palette.primary.main} />}
          title="Commission Rate"
          value={`${initialBusinessInfo.commissionRate}%`}
          subtitle="Per transaction"
        />
      </Box>

      <Paper sx={{ 
        p: 3, 
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        borderLeft: '4px solid #10b981'
      }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: '#1e293b' }}>
          Payout History
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Payout ID</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payouts.map((payout, index) => (
                <Fade key={payout.id} in timeout={200 * (index + 1)}>
                  <TableRow 
                    sx={{ 
                      '&:hover': { 
                        backgroundColor: 'rgba(148, 163, 184, 0.05)',
                      }
                    }}
                  >
                    <TableCell>{payout.id}</TableCell>
                    <TableCell>{new Date(payout.date).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#059669' }}>
                        ${payout.amount.toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={payout.status} 
                        color={getStatusChipColor(payout.status)} 
                        size="small"
                        sx={{ fontWeight: 500 }}
                      />
                    </TableCell>
                  </TableRow>
                </Fade>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Paper sx={{ 
        p: 3, 
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        borderLeft: '4px solid #f59e0b'
      }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: '#1e293b' }}>
          Generate Financial Report
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField 
            label="Start Date" 
            type="date" 
            size="small" 
            InputLabelProps={{ shrink: true }} 
            sx={{ minWidth: '180px' }}
          />
          <TextField 
            label="End Date" 
            type="date" 
            size="small" 
            InputLabelProps={{ shrink: true }} 
            sx={{ minWidth: '180px' }}
          />
          <Button 
            variant="contained"
            sx={{ 
              borderRadius: 2,
              background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
              px: 3
            }}
            onClick={() => showSnackbar('Report generation (mock)', 'info')}
          >
            Generate Report
          </Button>
        </Box>
        <Box sx={{ 
          height: 200, 
          mt: 3, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          backgroundColor: '#f8fafc', 
          borderRadius: 3 
        }}>
          <BarChart2 size={48} color="#64748b" />
          <Typography sx={{ ml: 1, color: '#64748b' }}>Financial report visualization</Typography>
        </Box>
      </Paper>
    </Box>
  );
};

const StoreSettingsSection: React.FC<SectionProps> = ({ showSnackbar }) => {
  const [businessInfo, setBusinessInfo] = useState<BusinessInfo>(initialBusinessInfo);
  const [storeHours, setStoreHours] = useState<StoreHours[]>(initialStoreHours);
  const [deliveryZoneRadius, setDeliveryZoneRadius] = useState(5);

  const handleInfoChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => 
    setBusinessInfo({...businessInfo, [e.target.name]: e.target.value });
  
  const handleHourChange = (day: StoreHours['day'], field: keyof StoreHours, value: string | boolean) => 
    setStoreHours(currentHours => currentHours.map(h => 
      h.day === day ? {...h, [field]: value} : h
    ));
  
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b', mb: 1 }}>
            Store Settings
          </Typography>
          <Typography variant="body1" sx={{ color: '#64748b' }}>
            Configure your store preferences
          </Typography>
        </Box>
      </Box>

      <Paper sx={{ 
        p: 3, 
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        borderLeft: '4px solid #8b5cf6'
      }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: '#1e293b' }}>
          Business Information
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <TextField 
              name="name" 
              label="Store Name" 
              value={businessInfo.name} 
              onChange={handleInfoChange} 
              fullWidth 
            />
            <TextField 
              name="phone" 
              label="Phone Number" 
              value={businessInfo.phone} 
              onChange={handleInfoChange} 
              fullWidth 
            />
            </Box>
            
            <TextField 
            name="email" 
            label="Contact Email" 
            value={businessInfo.email} 
            onChange={handleInfoChange} 
            fullWidth 
            />
            
            <TextField 
            name="address" 
            label="Address" 
            value={businessInfo.address} 
            onChange={handleInfoChange} 
            fullWidth 
            multiline
            rows={2}
            />
            
            <TextField 
            name="description" 
            label="Store Description" 
            value={businessInfo.description} 
            onChange={handleInfoChange} 
            fullWidth 
            multiline 
            rows={4} 
            />
            
          <Button 
            variant="contained"
            sx={{ 
              alignSelf: 'flex-start',
              borderRadius: 2,
              background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
              px: 3
            }}
            onClick={() => showSnackbar('Business info saved (mock)', 'success')}
          >
            Save Business Info
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ 
        p: 3, 
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        borderLeft: '4px solid #ec4899'
      }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: '#1e293b' }}>
          Business Hours
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {storeHours.map(hour => (
            <Box 
              key={hour.day} 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2, 
                flexWrap: 'wrap' 
              }}
            >
              <Typography sx={{ width: '100px', fontWeight: 500 }}>{hour.day}</Typography>
              <Switch 
                checked={hour.isOpen} 
                onChange={(e) => handleHourChange(hour.day, 'isOpen', e.target.checked)} 
                size="small"
                color="primary"
              />
              <TextField 
                label="Open Time" 
                type="time" 
                value={hour.openTime} 
                disabled={!hour.isOpen} 
                onChange={(e) => handleHourChange(hour.day, 'openTime', e.target.value)} 
                size="small" 
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: '120px' }}
              />
              <TextField 
                label="Close Time" 
                type="time" 
                value={hour.closeTime} 
                disabled={!hour.isOpen} 
                onChange={(e) => handleHourChange(hour.day, 'closeTime', e.target.value)} 
                size="small" 
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: '120px' }}
              />
            </Box>
          ))}
          
          <Button 
            variant="contained"
            sx={{ 
              alignSelf: 'flex-start',
              borderRadius: 2,
              background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
              px: 3
            }}
            onClick={() => showSnackbar('Business hours saved (mock)', 'success')}
          >
            Save Business Hours
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ 
        p: 3, 
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        borderLeft: '4px solid #14b8a6'
      }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: '#1e293b' }}>
          Delivery Settings
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField 
            label="Delivery Radius (km)" 
            type="number" 
            value={deliveryZoneRadius} 
            onChange={(e) => setDeliveryZoneRadius(Math.max(0, parseInt(e.target.value)))} 
            sx={{ mb: 2, maxWidth: '200px' }} 
            InputProps={{ endAdornment: 'km' }}
          />
          
          <Typography variant="body2" sx={{ mb: 2, color: '#64748b' }}>
            Current delivery zone is a circle with a {deliveryZoneRadius}km radius around your store address.
          </Typography>
          
          <Box sx={{ 
            height: '200px', 
            backgroundColor: '#f8fafc', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            borderRadius: 3,
            border: '1px dashed #cbd5e1'
          }}>
            <MapPin size={48} color="#64748b" />
            <Typography sx={{ ml: 1, color: '#64748b' }}>
              Map Preview Placeholder
            </Typography>
          </Box>
          
          <Button 
            variant="contained"
            sx={{ 
              alignSelf: 'flex-start',
              borderRadius: 2,
              background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
              px: 3
            }}
            onClick={() => showSnackbar('Delivery settings saved (mock)', 'success')}
          >
            Save Delivery Settings
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

const ProfileManagementSection: React.FC<SectionProps> = ({ showSnackbar, showConfirmDialog }) => {
  const [businessInfo, setBusinessInfo] = useState<BusinessInfo>(initialBusinessInfo);
  const [showPasswordFields, setShowPasswordFields] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  
  const handleInfoChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => 
    setBusinessInfo({...businessInfo, [e.target.name]: e.target.value });
  
  const handlePasswordChange = () => { 
    if (newPassword !== confirmNewPassword) { 
      showSnackbar("New passwords do not match!", 'error'); 
      return; 
    } 
    if (!currentPassword || !newPassword) { 
      showSnackbar("Please fill all password fields.", 'warning'); 
      return; 
    } 
    showSnackbar("Password changed successfully.", 'success'); 
    setShowPasswordFields(false); 
    setCurrentPassword(''); 
    setNewPassword(''); 
    setConfirmNewPassword(''); 
  };
  
  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => { 
    if (event.target.files && event.target.files[0]) { 
      const reader = new FileReader(); 
      reader.onload = (e) => { 
        setBusinessInfo({ ...businessInfo, logoUrl: e.target?.result as string }); 
        showSnackbar('Logo updated!', 'success');
      }; 
      reader.readAsDataURL(event.target.files[0]); 
    }
  };
  
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e293b', mb: 1 }}>
            Account & Profile
          </Typography>
          <Typography variant="body1" sx={{ color: '#64748b' }}>
            Manage your account settings
          </Typography>
        </Box>
      </Box>

      <Paper sx={{ 
        p: 3, 
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        borderLeft: '4px solid #0ea5e9'
      }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: '#1e293b' }}>
          Business Profile
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
            <Avatar 
              src={businessInfo.logoUrl} 
              sx={{ 
                width: 100, 
                height: 100, 
                borderRadius: 3,
                border: '3px solid #e2e8f0'
              }}
            />
            <Button 
              variant="outlined" 
              component="label" 
              startIcon={<ImagePlus size={18}/>}
            >
              Upload Logo
              <input type="file" hidden accept="image/*" onChange={handleLogoUpload} />
            </Button>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <TextField 
              name="name" 
              label="Business Name" 
              value={businessInfo.name} 
              onChange={handleInfoChange} 
              fullWidth 
            />
            <TextField 
              name="email" 
              label="Contact Email" 
              value={businessInfo.email} 
              onChange={handleInfoChange} 
              fullWidth 
              disabled
            />
          </Box>
          
          <TextField 
            name="phone" 
            label="Phone Number" 
            value={businessInfo.phone} 
            onChange={handleInfoChange} 
            fullWidth 
          />
          
          <TextField 
            name="address" 
            label="Address" 
            value={businessInfo.address} 
            onChange={handleInfoChange} 
            fullWidth 
            multiline
            rows={2}
          />
          
          <TextField 
            name="description" 
            label="Business Description" 
            value={businessInfo.description} 
            onChange={handleInfoChange} 
            fullWidth 
            multiline 
            rows={4} 
          />
          
          <Button 
            variant="contained"
            sx={{ 
              alignSelf: 'flex-start',
              borderRadius: 2,
              background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
              px: 3
            }}
            onClick={() => showSnackbar('Profile info saved (mock)', 'success')}
          >
            Save Profile Information
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ 
        p: 3, 
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        borderLeft: '4px solid #f97316'
      }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: '#1e293b' }}>
          Account Verification
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            Current Status:
          </Typography>
          <Chip
            label={businessInfo.verificationStatus}
            color={
              businessInfo.verificationStatus === 'Verified' ? 'success' :
              businessInfo.verificationStatus === 'Pending' ? 'warning' :
              'error'
            }
            size="small"
            sx={{ fontWeight: 500, mb: 1 }}
          />
          {businessInfo.verificationStatus === 'Pending' && (
            <Button
              variant="contained"
              sx={{ alignSelf: 'flex-start' }}
              onClick={() => showSnackbar('Verification email resent.', 'info')}
            >
              Resend Verification Email
            </Button>
          )}
          {(businessInfo.verificationStatus === 'Pending' || businessInfo.verificationStatus === 'Rejected') && (
            <Button
              variant="outlined"
              component="label"
              sx={{ alignSelf: 'flex-start' }}
            >
              Upload Documents
              <input hidden type="file" accept="application/pdf,image/*" />
            </Button>
          )}
          {businessInfo.verificationStatus === 'Rejected' && (
            <Typography variant="body2" sx={{ color: '#ef4444' }}>
              Your verification was rejected. Please upload valid documents.
            </Typography>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

// ======================================
// Global Alert Provider
// ======================================
export const GlobalAlertProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [snackbar, setSnackbar] = useState<SnackbarState>({ open: false, message: '', severity: 'info' });
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState>({ open: false, title: '', message: '', onConfirm: () => {} });
  const showSnackbar = (message: string, severity: AlertColor = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const showConfirmDialog = (title: string, message: string, onConfirm: () => void) => {
    setConfirmDialog({ open: true, title, message, onConfirm });
  };

  const handleCloseSnackbar = () => setSnackbar(prev => ({ ...prev, open: false }));
  const handleConfirm = () => {
    confirmDialog.onConfirm();
    setConfirmDialog(prev => ({ ...prev, open: false }));
  };
  const handleCancel = () => setConfirmDialog(prev => ({ ...prev, open: false }));

  return (
    <GlobalAlertContext.Provider value={{ showSnackbar, showConfirmDialog }}>
      {children}

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={3000} 
        onClose={handleCloseSnackbar} 
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog open={confirmDialog.open} onClose={handleCancel} maxWidth="xs" fullWidth>
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmDialog.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button onClick={handleConfirm} autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </GlobalAlertContext.Provider>
  );
};

// ======================================
// Main App Component
// ======================================

// Update the styles for the main App component
const App: React.FC = () => {
  const [drawerOpen, setDrawerOpen] = useState(true);
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const [selectedSection, setSelectedSection] = useState<string>('dashboard');

  const navigateTo = (section: string) => {
    setSelectedSection(section);
    if (isMobile) setDrawerOpen(false);
  };

  const sectionProps = useGlobalAlerts();

  const renderSection = () => {
    switch (selectedSection) {
      case 'dashboard': return <DashboardOverviewSection {...sectionProps} navigateTo={navigateTo} />;
      case 'products': return <ProductManagementSection {...sectionProps} />;
      case 'orders': return <OrderManagementSection {...sectionProps} />;
      case 'financial': return <FinancialManagementSection {...sectionProps} />;
      case 'settings': return <StoreSettingsSection {...sectionProps} />;
      case 'profile': return <ProfileManagementSection {...sectionProps} />;
      default: return <DashboardOverviewSection {...sectionProps} navigateTo={navigateTo} />;
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <GlobalAlertProvider>
        <CssBaseline />
        <Box 
          sx={{ 
            display: 'flex',
            height: 'calc(100% - 128px)', // Adjust based on your header/footer height
            position: 'relative',
            margin: '64px 0', // Adjust based on your header height
          }}
        >
          <Drawer
            variant={isMobile ? 'temporary' : 'permanent'}
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            sx={{
              position: 'relative',
              '& .MuiDrawer-paper': { 
                position: 'relative',
                width: drawerWidth, 
                boxSizing: 'border-box',
                backgroundColor: '#f8fafc',
                border: 'none',
                borderRight: '1px solid #e2e8f0',
                height: '100%',
                overflowY: 'auto'
              }
            }}
            PaperProps={{
              elevation: 0
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
                  Dashboard
                </Typography>
                {isMobile && (
                  <IconButton onClick={() => setDrawerOpen(false)}>
                    <ChevronLeft />
                  </IconButton>
                )}
              </Box>
              <Divider />
              <List sx={{ flexGrow: 1, py: 2 }}>
                <ListItem disablePadding>
                  <ListItemButton 
                    selected={selectedSection === 'dashboard'} 
                    onClick={() => navigateTo('dashboard')}
                  >
                    <ListItemIcon><LayoutDashboard /></ListItemIcon>
                    <ListItemText primary="Dashboard Overview" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton 
                    selected={selectedSection === 'products'} 
                    onClick={() => navigateTo('products')}
                  >
                    <ListItemIcon><Package /></ListItemIcon>
                    <ListItemText primary="Product Management" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton 
                    selected={selectedSection === 'orders'} 
                    onClick={() => navigateTo('orders')}
                  >
                    <ListItemIcon><ShoppingCart /></ListItemIcon>
                    <ListItemText primary="Order Management" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton 
                    selected={selectedSection === 'financial'} 
                    onClick={() => navigateTo('financial')}
                  >
                    <ListItemIcon><DollarSign /></ListItemIcon>
                    <ListItemText primary="Financial Management" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton 
                    selected={selectedSection === 'settings'} 
                    onClick={() => navigateTo('settings')}
                  >
                    <ListItemIcon><Settings /></ListItemIcon>
                    <ListItemText primary="Store Settings" />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton 
                    selected={selectedSection === 'profile'} 
                    onClick={() => navigateTo('profile')}
                  >
                    <ListItemIcon><UserCircle /></ListItemIcon>
                    <ListItemText primary="Account & Profile" />
                  </ListItemButton>
                </ListItem>
              </List>
            </Box>
          </Drawer>

          <Box 
            component="main" 
            sx={{ 
              flexGrow: 1,
              p: 3,
              height: '100%',
              overflowY: 'auto',
              backgroundColor: '#f8fafc'
            }}
          >
            {renderSection()}
          </Box>
        </Box>
      </GlobalAlertProvider>
    </ThemeProvider>
  );
};

export default App;